import Button from "@commons/Button";
import ComboBox from "@commons/Input/ComboBox";
import SelectInput from "@commons/Input/Select";
import TextInput from "@commons/Input/Text";
import MapTableSelector from "@commons/Table/TableHeader/MapTableSelector";
import Text from "@commons/Typography/Text";
import SearchIcon from "@mui/icons-material/Search";
import { searchIndividuals } from "@services/individuals/search";
import type { SamplesFilters } from "@services/samples/list/type";
import { searchTaxonomic } from "@services/taxonomicGroups/search";
import { searchPpmaUsers } from "@services/users/search";
import type { ComboBoxOption } from "@typings/components/comboBox";
import type { Individual } from "@typings/entities/individuals";
import { SAMPLE_TYPE_OPTIONS } from "@typings/entities/samples/enums";
import type { TaxonomicGroup } from "@typings/entities/taxonomicGroup";
import { formatOption } from "@utils/formatters";
import { useState } from "react";

type SamplesFilterProps = {
  filters: SamplesFilters;
  onChange: (field: keyof SamplesFilters, value: string) => void;
  onApply: () => void;
};

function SamplesFilter({ filters, onChange, onApply }: SamplesFilterProps) {
  const [selectedUser, setSelectedUser] = useState<ComboBoxOption | null>(null);
  const [selectedIndividual, setSelectedIndividual] =
    useState<ComboBoxOption<Individual> | null>(null);
  const [selectedTaxonomicGroup, setSelectedTaxonomicGroup] =
    useState<ComboBoxOption<TaxonomicGroup> | null>(null);

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="flex justify-center xl:justify-start">
          <MapTableSelector />
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7 gap-4 w-full">
          <div className="w-full sm:min-w-36">
            <SelectInput
              size="small"
              label="Tipo de Amostra"
              value={filters.type}
              onChange={(e) => onChange("type", e.target.value as string)}
              options={SAMPLE_TYPE_OPTIONS}
              fullWidth
            />
          </div>

          <div className="w-full sm:min-w-36 col-span-1 ">
            <ComboBox
              value={selectedUser}
              onChange={(option) => {
                setSelectedUser(option);
                onChange("reporterId", formatOption(option).value);
              }}
              searchService={searchPpmaUsers}
              label="Coletor"
              minSearchLength={0}
              debounceTime={300}
            />
          </div>

          <div className="w-full sm:min-w-36  col-span-1">
            <ComboBox<Individual>
              value={selectedIndividual}
              onChange={(option) => {
                setSelectedIndividual(option);
                onChange("individualId", option?.item?.id ?? "");
              }}
              searchService={searchIndividuals}
              label="Indivíduo"
              minSearchLength={0}
              debounceTime={300}
            />
          </div>

          <div className="w-full sm:min-w-36  col-span-1">
            <ComboBox<TaxonomicGroup>
              value={selectedTaxonomicGroup}
              onChange={(option) => {
                setSelectedTaxonomicGroup(option);
                onChange("taxonomicGroupId", option?.item?.id ?? "");
              }}
              searchService={searchTaxonomic}
              label="Espécie"
              minSearchLength={0}
              debounceTime={300}
            />
          </div>

          <div className="w-full col-span-1 xs:col-span-2">
            <div className="mb-2">
              <Text>Período da Amostra</Text>
            </div>
            <div className="flex flex-wrap xs:flex-nowrap gap-4">
              <TextInput
                textSize="small"
                type="datetime-local"
                value={filters.sampleDateStart}
                onChange={(e) => onChange("sampleDateStart", e.target.value)}
                fullWidth
              />
              <TextInput
                textSize="small"
                type="datetime-local"
                value={filters.sampleDateEnd}
                onChange={(e) => onChange("sampleDateEnd", e.target.value)}
                fullWidth
              />
            </div>
          </div>

          <div className="w-full flex items-end col-span-1 xs:col-span-2 2xl:col-span-1">
            <Button
              startIcon={<SearchIcon />}
              customVariant="primary"
              size="full"
              onClick={onApply}
            >
              <Text>Filtrar</Text>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
export default SamplesFilter;
